import React, { useState, useEffect, useCallback } from "react";
import "./Flights.css";
import Sidebar from "../Sidebar/Sidebar";
import { fetchDashboardSummary, fetchPilotsAndVehicles, fetchTeamRequests } from "../Apis/AdminApis";
import pavamanLogo from "../images/drone.png";
import { useNavigate } from "react-router-dom";

const Flights = ({ isModal = false, onClose }) => {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState("week");
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [stats, setStats] = useState({
    acres: "0.00",
    flights: "0",
    flightTime: "0 h 0 m",
    pilotsWorked: "0"
  });
  const [selectedPilot, setSelectedPilot] = useState("");
  const [selectedDrone, setSelectedDrone] = useState("");
  const [pilotsList, setPilotsList] = useState([]);
  const [dronesList, setDronesList] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [xAxisLabels, setXAxisLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [error, setError] = useState(null);
  const [isFilterChanging, setIsFilterChanging] = useState(false);

useEffect(() => {
  const today = new Date().toISOString().split('T')[0];
  setSelectedDate(today);

  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
  setEndDate(today);

  loadPilots();              
  loadPilotsAndVehicles();  
}, []);

const loadPilotsAndVehicles = async () => {
  try {
    const data = await fetchPilotsAndVehicles();

    if (data?.error) {
      // Session expired
      if (data?.error?.includes("Unauthorized")) {
        setError("Session expired, redirecting to login page");
        setTimeout(() => {
          navigate("/login");
        }, 2000);
        return;
      }
      setError(data.error);
      return;
    }

    if (data?.vehicles && Array.isArray(data.vehicles)) {
      setDronesList(data.vehicles);
    } else {
      setDronesList([]);
    }
  } catch (err) {
    console.error("Error loading vehicles:", err);
    
    if (err?.response?.status === 401 || err?.response?.data?.error?.includes("Unauthorized")) {
      setError("Session expired, redirecting to login page");
      setTimeout(() => {
        navigate("/login");
      }, 2000);
      return;
    }
    
    setDronesList([]);
  }
};

const loadPilots = async () => {
  try {
    const data = await fetchTeamRequests({});

    if (data?.error) {
      // Session expired
      if (data?.error?.includes("Unauthorized")) {
        setError("Session expired, redirecting to login page");
        setTimeout(() => {
          navigate("/login");
        }, 2000);
        return;
      }
      setError(data.error);
      return;
    }

    if (data?.pilots && Array.isArray(data.pilots)) {
      setPilotsList(data.pilots);
    } else {
      setPilotsList([]);
    }
  } catch (err) {
    console.error("Error loading pilots:", err);
    
    if (err?.response?.status === 401 || err?.response?.data?.error?.includes("Unauthorized")) {
      setError("Session expired, redirecting to login page");
      setTimeout(() => {
        navigate("/login");
      }, 2000);
      return;
    }
    
    setPilotsList([]);
  }
};

useEffect(() => {
  if (!selectedPilot) {
    loadPilotsAndVehicles();
    return;
  }

  const filtered = dronesList.filter(
    (drone) =>
      drone.initially_assigned_pilot_name &&
      pilotsList.find(
        (p) =>
          p.email === selectedPilot &&
          drone.initially_assigned_pilot_name.toLowerCase().includes(p.name.toLowerCase())
      )
  );

  setDronesList(filtered);
}, [selectedPilot]);

  useEffect(() => {
    if (activeFilter === "custom") {
      if (startDate && endDate) {
        loadFlightData();
      }
    } else {
      if (selectedDate) {
        loadFlightData();
      }
    }
  }, [activeFilter, selectedDate, selectedPilot, selectedDrone, startDate, endDate]);
 
const loadFlightData = async () => {
  setIsFilterChanging(true);
  setError(null);

  try {
    const payload = {
      filter_type: activeFilter === "all" ? "year" : activeFilter,
    };

    if (activeFilter === "custom") {
      payload.start_date = startDate;
      payload.end_date = endDate;
    } else {
      payload.date = selectedDate;
    }

    if (selectedPilot) {
      payload.pilot_email = selectedPilot;
    }

    if (selectedDrone) {
      payload.drone_id = selectedDrone;
    }

    const response = await fetchDashboardSummary(payload);
    console.log("FULL API RESPONSE:", response);

    // Session expiry check
    if (response?.error) {
      if (response?.error?.includes("Unauthorized")) {
        setError("Session expired, redirecting to login page");
        setTimeout(() => {
          navigate("/login");
        }, 2000);
        return;
      }
      setError(response?.error || "Failed to load flight data");
      return;
    }

    if (response && !response.error) {
      let chartDataFromApi = null;
      let summaryDataFromApi = null;
      const dashboard = response.dashboard || response;
      const source =
        dashboard.superadmin_company ||
        dashboard.admin ||
        dashboard.admin_companies ||
        dashboard.view_as_admin ||
        dashboard;

      chartDataFromApi = source?.chart || null;
      summaryDataFromApi = source?.cards || null;
      if (chartDataFromApi) {
        const transformedData = {
          data: chartDataFromApi,
          summary: {
            total_missions: summaryDataFromApi?.total_missions || 0,
            total_sprayed_acres: summaryDataFromApi?.total_sprayed_acres || 0,
            total_mission_hours: summaryDataFromApi?.total_mission_hours || 0,
            active_pilots: summaryDataFromApi?.active_pilots || 0
          }
        };

        updateStats(transformedData);
        transformChartData(transformedData);
      } else {
        console.warn("No chart data found");
        setChartData([]);
      }

    } else {
      setError(response?.error || "Failed to load flight data");
    }

  } catch (err) {
    console.error("Error loading flight data:", err);
    
    // Check for unauthorized error in catch block
    if (err?.response?.status === 401 || err?.response?.data?.error?.includes("Unauthorized")) {
      setError("Session expired, redirecting to login page");
      setTimeout(() => {
        navigate("/login");
      }, 2000);
      return;
    }
    
    setError(err.response?.data?.error || "Failed to load flight data");
  } finally {
    setLoading(false);
    setIsFilterChanging(false);
    if (!initialLoadDone) {
      setInitialLoadDone(true);
    }
  }
};


  const updateStats = (apiData) => {
    const summary = apiData.summary || {};
    const totalHours = parseFloat(summary.total_mission_hours) || 0;
    const hours = Math.floor(totalHours);
    const minutes = Math.round((totalHours - hours) * 60);

    setStats({
      acres: summary.total_sprayed_acres ? parseFloat(summary.total_sprayed_acres).toFixed(2) : "0.00",
      flights: summary.total_missions?.toString() || "0",
      flightTime: `${hours} h ${minutes} m`,
      pilotsWorked: summary.active_pilots?.toString() || "0"
    });
  };

  const transformChartData = (apiData) => {
    if (!apiData?.data || Object.keys(apiData.data).length === 0) {
      setChartData([]);
      setXAxisLabels([]);
      return;
    }

    let keys = Object.keys(apiData.data);
    if (activeFilter === "year" || activeFilter === "all") {
      const monthOrder = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      keys.sort((a, b) => monthOrder.indexOf(a) - monthOrder.indexOf(b));
    } else if (/^\d{4}$/.test(keys[0])) {
      keys.sort((a, b) => Number(a) - Number(b));
    } else {
      keys.sort((a, b) => new Date(a) - new Date(b));
    }

    const transformed = keys.map((key) => ({
      raw: key,
      label: formatLabel(key),
      value: apiData.data[key] || 0,
      flights: apiData.data[key] || 0
    }));

    setChartData(transformed);
    setXAxisLabels(transformed.map(item => item.label));
  };

  const formatLabel = (key) => {
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    if (monthNames.includes(key)) {
      return key;
    }
    if (/^\d{4}$/.test(key)) {
      return key;
    }
    const date = new Date(key);
    if (!isNaN(date)) {
      if (activeFilter === "week") {
        return date.toLocaleDateString("en-US", { weekday: 'short' });
      }
      return date.toLocaleDateString("en-US", { day: "2-digit", month: "short" });
    }
    
    return key;
  };

  const handleFilterChange = (filter) => {
    if (filter === activeFilter) return;
    setActiveFilter(filter);
    setLoading(true);
    setChartData([]);
    if (filter !== "custom") {
      setSelectedDate(new Date().toISOString().split("T")[0]);
    }
  };

  const handlePilotChange = (e) => {
    const value = e.target.value;
    setSelectedPilot(value);
    setSelectedDrone(""); 
    setLoading(true);
    setChartData([]);
  };

  const handleDroneChange = (e) => {
    setSelectedDrone(e.target.value);
    setLoading(true);
    setChartData([]);
  };

  const chartWidth = 1000;
  const chartHeight = 400;
  const chartPadding = { top: 30, right: 30, bottom: 60, left: 60 };
  const innerWidth = chartWidth - chartPadding.left - chartPadding.right;
  const innerHeight = chartHeight - chartPadding.top - chartPadding.bottom;
  const rawMaxValue = Math.max(...chartData.map(d => d.value || 0), 1);
  
  const getNiceMax = (value) => {
    if (value <= 0) return 10;
    const exponent = Math.floor(Math.log10(value));
    const fraction = value / Math.pow(10, exponent);
    let niceFraction;
    if (fraction <= 1) niceFraction = 1;
    else if (fraction <= 2) niceFraction = 2;
    else if (fraction <= 5) niceFraction = 5;
    else niceFraction = 10;
    return niceFraction * Math.pow(10, exponent);
  };

  const maxValue = getNiceMax(rawMaxValue);
  const steps = 5;
  const yAxisValues = Array.from({ length: steps + 1 }, (_, i) => 
    Math.round((maxValue / steps) * i)
  );

  const getPoints = () => {
    if (chartData.length === 0) return [];
    return chartData.map((point, index) => {
      const x = chartPadding.left + (chartData.length > 1 
        ? (index / (chartData.length - 1)) * innerWidth 
        : innerWidth / 2);
      const y = chartPadding.top + innerHeight - (point.value / maxValue) * innerHeight;
      return { x, y, value: point.value, label: point.label };
    });
  };

  const points = getPoints();
  
  const getLinePath = () => {
    if (points.length === 0) return "";
    if (points.length === 1) {
      return `M ${points[0].x} ${points[0].y}`;
    }
    let path = `M ${points[0].x} ${points[0].y}`;
    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1];
      const curr = points[i];
      const cp1x = prev.x + (curr.x - prev.x) / 3;
      const cp1y = prev.y;
      const cp2x = prev.x + (curr.x - prev.x) * 2 / 3;
      const cp2y = curr.y;
      path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${curr.x} ${curr.y}`;
    }
    return path;
  };

  const getAreaPath = () => {
    if (points.length === 0) return "";
    const linePath = getLinePath();
    return linePath + 
      ` L ${points[points.length - 1].x} ${chartPadding.top + innerHeight}` +
      ` L ${chartPadding.left} ${chartPadding.top + innerHeight} Z`;
  };
  const showLoading = loading || isFilterChanging || (!initialLoadDone && !error);

  return (
    <div className={`dashboard-wrapper ${isModal ? 'modal-mode' : ''}`}>
      {!isModal && <Sidebar />}
      
      <div className={`dashboard-content ${isModal ? 'modal-version' : ''}`}>
        <div className="dashboard-header">
          <div className="header-left">
            <h1 className="dashboard-title">Flight Analytics</h1>
            <p className="dashboard-subtitle">Complete flights Analytics and details</p>
          </div>
          
          <div className="header-right">
            <button 
              className="back-btn"
              onClick={() => navigate("/admin/dashboard")}
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>

        {isModal && (
          <div className="modal-header-row">
            <h2 className="flights-title">Flight Statistics</h2>
          </div>
        )}

        <div className="filters-section">
          <div className="filter-group">
            <label className="filter-label">Select Pilot</label>
            <select 
              className="filter-select"
              value={selectedPilot}
              onChange={handlePilotChange}
            >
              <option value="">All Pilots</option>
              {pilotsList.map((pilot, index) => (
               <option key={pilot.id} value={pilot.email}>
  {pilot.email}
</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Select Drone</label>
            <select
              className="filter-select"
              value={selectedDrone}
              onChange={handleDroneChange}
            >
              <option value="">
                {selectedPilot ? "All Drones for Selected Pilot" : "All Drones"}
              </option>
              {dronesList.map((drone, index) => (
                <option key={drone.vehicle_id || index} value={drone.vehicle_id}>
                  {drone.vehicle_name
                    ? `${drone.vehicle_name} (${drone.vehicle_id})`
                    : `Drone ${drone.vehicle_id}`}
                </option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <label className="filter-label">Duration & Time</label>
            <div className="duration-buttons">
              {['day', 'week', 'month', 'all', 'custom'].map((filter) => (
                <button 
                  key={filter}
                  className={`duration-btn ${activeFilter === filter ? 'active' : ''}`}
                  onClick={() => handleFilterChange(filter)}
                >
                  {filter === 'all' ? 'Yearly' : filter.charAt(0).toUpperCase() + filter.slice(1)}
                </button>
              ))}
            </div>
            
            <div className="date-inputs">
              {activeFilter === 'custom' ? (
                <>
                  <input
                    type="date"
                    className="date-input"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                  <span className="date-separator">to</span>
                  <input
                    type="date"
                    className="date-input"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </>
              ) : activeFilter !== 'all' ? (
                <input
                  type="date"
                  className="date-input single"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                />
              ) : null}
            </div>
          </div>
        </div>

        <div className="chart-section">
          <div className="chart-container">
            {showLoading ? (
              <div className="custom-loading-container">
                <img src={pavamanLogo} alt="Loading" className="spinning-logo" />
                <p className="loading-text">Loading flight data...</p>
              </div>
            ) : error ? (
              <div className="error-chart">{error}</div>
            ) : chartData.length === 0 ? (
              <div className="no-data-message">No flight data available for the selected filters</div>
            ) : (
              <div className="chart-wrapper">
                 
                  <svg
                    className="flight-chart"
                    viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                    preserveAspectRatio="xMidYMid meet"
                  >
                    <defs>
                      <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#4CAF50" stopOpacity="0.8" />
                        <stop offset="100%" stopColor="#4CAF50" stopOpacity="0.1" />
                      </linearGradient>
                    </defs>

                    {yAxisValues.map((value) => {
                      const y = chartPadding.top + innerHeight - (value / maxValue) * innerHeight;
                      return (
                        <g key={value}>
                          <line
                            x1={chartPadding.left}
                            y1={y}
                            x2={chartWidth - chartPadding.right}
                            y2={y}
                            stroke="#e0e0e0"
                            strokeWidth="1"
                            strokeDasharray="5,5"
                          />
                        </g>
                      );
                    })}

                    <line
                      x1={chartPadding.left}
                      y1={chartPadding.top + innerHeight}
                      x2={chartWidth - chartPadding.right}
                      y2={chartPadding.top + innerHeight}
                      stroke="#333"
                      strokeWidth="2"
                    />
                    <line
                      x1={chartPadding.left}
                      y1={chartPadding.top}
                      x2={chartPadding.left}
                      y2={chartPadding.top + innerHeight}
                      stroke="#333"
                      strokeWidth="2"
                    />

                    {yAxisValues.map((value) => {
                      const y = chartPadding.top + innerHeight - (value / maxValue) * innerHeight;
                      return (
                        <text
                          key={value}
                          x={chartPadding.left - 10}
                          y={y}
                          textAnchor="end"
                          dominantBaseline="middle"
                          className="axis-label"
                        >
                          {value}
                        </text>
                      );
                    })}

                    {points.length > 0 && (
                      <path d={getAreaPath()} fill="url(#areaGradient)" opacity="0.3" />
                    )}
                    {points.length > 0 && (
                      <path
                        d={getLinePath()}
                        fill="none"
                        stroke="#4CAF50"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    )}

                    {points.map((point, index) => (
                      <g key={`${point.label}-${index}`} className="data-point-group">
                        <circle
                          cx={point.x}
                          cy={point.y}
                          r="6"
                          fill="#4CAF50"
                          stroke="white"
                          strokeWidth="2"
                          className="data-point"
                        />
                        <text
                          x={point.x}
                          y={point.y - 20}
                          textAnchor="middle"
                          className="point-value"
                        >
                          {point.value}
                        </text>
                      </g>
                    ))}

                    {points.map((point, index) => (
                      <text
                        key={`xlabel-${point.label}-${index}`}
                        x={point.x}
                        y={chartPadding.top + innerHeight + 20}
                        textAnchor="middle"
                        transform={`rotate(45, ${point.x}, ${chartPadding.top + innerHeight + 40})`}
                        className="x-axis-label"
                      >
                        {point.label}
                      </text>
                    ))}
                  </svg>
              </div>
            )}
          </div>
        </div>

        <div className="info-section">
          <p className="info-text">
            Showing flight data for{" "}
            {activeFilter === "all" && "yearly"}
            {activeFilter === "day" && "selected day"}
            {activeFilter === "week" && "selected week"}
            {activeFilter === "month" && "selected month"}
            {activeFilter === "custom" && startDate && endDate &&
              `${new Date(startDate).toLocaleDateString()} to ${new Date(endDate).toLocaleDateString()}`}
            {selectedPilot && ` • Pilot: ${pilotsList.find(p => p.email === selectedPilot)?.name || selectedPilot}`}
            {selectedDrone && ` • Drone: ${dronesList.find(drone => drone.vehicle_id === selectedDrone)?.vehicle_name || selectedDrone}`}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Flights;